'use client';

import { AppShell, Container, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect } from 'react';

import { AppFooter, AppHeader, NavBar } from '@/components';
import {
  AuthProvider,
  FooterProvider,
  HeaderProvider,
  PageTitleProvider,
} from '@/contexts';

import styles from './layout.module.css';

const AuthenticatedLayout = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();

  const [
    navBarIsOpenMobile,
    { toggle: toggleNavBarIsOpenMobile, close: closeNavBarMobile },
  ] = useDisclosure(false);
  const [
    navBarIsOpenDesktop,
    { toggle: toggleNavBarIsOpenDesktop, close: closeNavBarDesktop },
  ] = useDisclosure(true);

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // Add effect to close NavBar on route changes
  useEffect(() => {
    if (isMobile) {
      closeNavBarMobile();
    } else {
      closeNavBarDesktop();
    }
  }, [pathname, isMobile, closeNavBarMobile, closeNavBarDesktop]);

  const toggleNavBarIsOpen = useCallback(
    () => (isMobile ? toggleNavBarIsOpenMobile() : toggleNavBarIsOpenDesktop()),
    [isMobile, toggleNavBarIsOpenMobile, toggleNavBarIsOpenDesktop],
  );

  return (
    <AuthProvider>
      <HeaderProvider>
        <FooterProvider>
          <PageTitleProvider
            initialTitle={''}
            initialSecondaryTitle={null}
          >
            <AppShell
              header={{ height: { base: 86 } }}
              footer={{ height: { base: 96, md: 0 } }}
              layout="alt"
              aside={{
                width: 0,
                breakpoint: 'sm',
                collapsed: {
                  mobile: !navBarIsOpenMobile,
                  desktop: !navBarIsOpenDesktop,
                },
              }}
              p={0}
            >
              {/* TODO: move this styling */}
              <AppHeader toggleNavBarIsOpen={toggleNavBarIsOpen} />

              <AppShell.Main classNames={{ main: styles.appShellMain }}>
                <Container
                  size="xl"
                  p={0}
                  w="100%"
                  classNames={{ root: styles.appShellMainContainer }}
                >
                  {children}
                </Container>
              </AppShell.Main>

              <AppFooter />

              <NavBar
                isOpen={isMobile ? navBarIsOpenMobile : navBarIsOpenDesktop}
                onClose={
                  isMobile
                    ? toggleNavBarIsOpenMobile
                    : toggleNavBarIsOpenDesktop
                }
              />
            </AppShell>
          </PageTitleProvider>
        </FooterProvider>
      </HeaderProvider>
    </AuthProvider>
  );
};

export default AuthenticatedLayout;
